import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { PaginatedReader } from '@/components/PaginatedReader';
import { Button } from '@/components/ui/button';

interface ReaderModeViewProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
}

/**
 * Full-viewport paginated reading mode for a wiki article/publication
 * section — the display Wikistar offers for e-ink e-readers and
 * tablets, where a normal, endlessly-scrolling article page is a poor
 * fit: e-ink displays refresh slowly and look best showing a static,
 * fixed page rather than continuous scroll, and a "turn the page"
 * mental model (tap left/right thirds — see `PaginatedReader`) matches
 * how those devices are actually held and used.
 *
 * This deliberately renders as a plain fixed-position overlay rather
 * than a `Dialog` — a book-reading mode needs the *entire* viewport
 * (no backdrop, no rounded corners, no max-width), and disabling page
 * scroll behind it, neither of which the shared `Dialog` primitive is
 * meant for.
 *
 * `children` is the exact same rendered article/section body a normal
 * page would show (`ContentBody`, still wrapped in `HighlightLayer` by
 * the caller if highlight mode happens to be on) — only the
 * layout/navigation changes here, never the content itself.
 */
export function ReaderModeView({ title, children, onClose }: ReaderModeViewProps) {
  // A book-reading mode should feel like its own space, not a modal
  // floating over a scrollable page — lock background scroll while open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <p className="min-w-0 truncate text-sm font-medium">{title}</p>
        <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0" onClick={onClose} aria-label="Exit reader mode">
          <X className="size-4" />
        </Button>
      </header>

      <PaginatedReader className="flex-1">
        <div className="mx-auto max-w-2xl py-6 text-[17px]">{children}</div>
      </PaginatedReader>
    </div>
  );
}
